import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import MiniDashboard from "../components/MiniDashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, UserCog, ChevronRight, Award, ClipboardList, CheckCircle2 } from "lucide-react";

export default function Employees() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", role: "Sales Rep", notes: "" });

  const load = async () => {
    setLoading(true);
    const [r, o] = await Promise.all([api.get("/employees"), api.get("/orders")]);
    setItems(r.data);
    setOrders(o.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", form);
      toast.success("Employee added");
      setOpen(false);
      setForm({ name: "", phone: "", role: "Sales Rep", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Move this employee to the Recycle Bin? You can restore within 45 days.")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Moved to Recycle Bin");
    load();
  };

  const stats = useMemo(() => {
    const counts = {};
    let completed = 0;
    for (const o of orders) {
      if (o.employee_name) counts[o.employee_name] = (counts[o.employee_name] || 0) + 1;
      if (o.status === "completed") completed += 1;
    }
    const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return { total: orders.length, completed, top: ranked[0]?.[0] || "—" };
  }, [orders]);

  return (
    <div data-testid="employee-page">
      <PageHeader
        title="Employees"
        subtitle="Staff & reps — click a row to see order performance"
        action={
          <button onClick={() => setOpen(true)} data-testid="add-employee-btn" className="btn-primary flex items-center gap-2">
            <Plus className="h-5 w-5" /> Add Employee
          </button>
        }
      />

      <MiniDashboard
        testid="employee-mini-dashboard"
        stats={[
          { icon: UserCog, label: "Total Employees", value: items.length, tint: "forest" },
          { icon: ClipboardList, label: "Total Orders Handled", value: stats.total, tint: "info" },
          { icon: CheckCircle2, label: "Completed Orders", value: stats.completed, tint: "forest" },
          { icon: Award, label: "Top Performer", value: stats.top, tint: "accent" },
        ]}
      />

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-soft">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl bg-forest-soft text-forest grid place-items-center mb-3">
              <UserCog className="h-6 w-6" />
            </div>
            <div className="font-outfit font-semibold text-ink">No employees yet</div>
            <div className="text-ink-soft text-sm font-figtree">Add your first team member to start assigning buyers.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand/60">
                <tr>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Name</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Role</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Phone</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Notes</th>
                  <th className="text-right font-figtree font-semibold text-sm text-ink-soft px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="border-t border-line hover:bg-bone/60 cursor-pointer"
                    onClick={() => nav(`/employees/${it.id}`)}
                    data-testid={`employee-row-${it.id}`}
                  >
                    <td className="px-6 py-4 font-figtree font-medium text-ink">
                      <div className="flex items-center gap-2">
                        <span>{it.name}</span>
                        <ChevronRight className="h-4 w-4 text-ink-mute" />
                      </div>
                    </td>
                    <td className="px-6 py-4 font-figtree text-ink-soft">{it.role || "—"}</td>
                    <td className="px-6 py-4 font-figtree text-ink-soft">{it.phone || "—"}</td>
                    <td className="px-6 py-4 font-figtree text-ink-soft text-sm">{it.notes || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={(e) => del(it.id, e)} className="p-2 rounded-lg text-ink-soft hover:bg-red-50 hover:text-red-700 transition-colors duration-200">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-outfit">Add Employee</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Name</label>
              <input
                data-testid="employee-name-input"
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Role</label>
              <input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Notes</label>
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-line font-figtree">Cancel</button>
              <button data-testid="employee-submit-btn" type="submit" className="btn-primary">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
